import React from 'react';
import { ErrorBoundary } from '../ErrorBoundary';
import { Button } from '../../ui/Button';
import Link from 'next/link';

interface CheckoutErrorFallbackProps {
  error?: Error;
  errorInfo?: React.ErrorInfo;
  retry?: () => void;
}

/**
 * Checkout-specific error fallback UI
 */
const CheckoutErrorFallback: React.FC<CheckoutErrorFallbackProps> = ({ error, retry }) => {
  const handleResumeCheckout = () => {
    if (typeof window !== 'undefined') {
      // Try to resume from current URL or go to events
      const currentPath = window.location.pathname;
      if (currentPath.includes('/checkout') || currentPath.includes('/payment')) {
        window.location.reload();
      } else {
        window.location.href = '/events';
      }
    }
  };

  const handleClearCartAndStartOver = () => {
    if (typeof window !== 'undefined') {
      // Clear any stored cart/checkout data
      try {
        localStorage.removeItem('checkout-session');
        localStorage.removeItem('cart-items');
        sessionStorage.clear();
      } catch (e) {
        // Ignore localStorage errors
      }

      window.location.href = '/events';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow-lg rounded-lg sm:px-10">
          <div className="text-center">
            {/* Checkout Error Icon */}
            <div className="mx-auto h-16 w-16 text-orange-600 mb-6">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M3 3h2l.4 2M7 13h10l4-8H5.4m0 0L7 13m0 0l-1.1 5H19M7 13v8a2 2 0 002 2h10a2 2 0 002-2v-3"
                />
              </svg>
            </div>

            <h2 className="text-xl font-bold text-gray-900 mb-2">
              Checkout Error
            </h2>

            <p className="text-gray-600 mb-6 text-sm">
              We encountered an issue processing your order. Don&apos;t worry -
              your payment hasn&apos;t been processed yet. Please try again.
            </p>

            {/* Important Notice */}
            <div className="bg-yellow-50 border border-yellow-200 rounded p-4 mb-6">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-yellow-800">
                    <strong>Important:</strong> No payment has been processed.
                    You can safely try again or start over.
                  </p>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="space-y-3 mb-6">
              {retry && (
                <Button
                  onClick={retry}
                  variant="solid"
                  className="w-full bg-orange-600 hover:bg-orange-700"
                >
                  Try Again
                </Button>
              )}

              <Button
                onClick={handleResumeCheckout}
                variant="secondary"
                className="w-full"
              >
                Resume Checkout
              </Button>

              <Button
                onClick={handleClearCartAndStartOver}
                variant="outline"
                className="w-full text-red-600 border-red-300 hover:bg-red-50"
              >
                Start Over (Clear Cart)
              </Button>
            </div>

            {/* Alternative Actions */}
            <div className="border-t border-gray-200 pt-4">
              <h3 className="text-sm font-medium text-gray-700 mb-2">Other options:</h3>
              <div className="space-y-2 text-sm">
                <Link
                  href="/events"
                  className="block text-blue-600 hover:text-blue-800 hover:underline"
                >
                  Browse Events
                </Link>
                <Link
                  href="/"
                  className="block text-blue-600 hover:text-blue-800 hover:underline"
                >
                  Go to Homepage
                </Link>
              </div>
            </div>

            {/* Error Details for Development */}
            {process.env.NODE_ENV === 'development' && error && (
              <details className="mt-6 text-left bg-gray-50 p-4 rounded text-xs">
                <summary className="cursor-pointer font-medium text-gray-700">
                  Error Details (Development Only)
                </summary>
                <pre className="mt-2 whitespace-pre-wrap text-xs text-gray-600">
                  {error.message}
                  {error.stack}
                </pre>
              </details>
            )}

            {/* Support Information */}
            <div className="mt-6 pt-4 border-t border-gray-200 text-xs text-gray-500">
              <p className="mb-1">
                <strong>Need Help?</strong>
              </p>
              <p>
                If you continue to experience issues, please contact our{' '}
                <a
                  href="mailto:support@dramatickets.co.uk"
                  className="text-blue-600 hover:text-blue-800 hover:underline"
                >
                  support team
                </a>
                {' '}with your order details.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Checkout Error Boundary Component
 * Specialized for checkout and payment flow error handling
 */
export const CheckoutErrorBoundary: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <ErrorBoundary
      fallback={CheckoutErrorFallback}
      onError={(error, errorInfo) => {
        // Log checkout-specific errors with payment context
        console.error('[CheckoutErrorBoundary] Checkout error:', error);
        console.error('[CheckoutErrorBoundary] Error info:', errorInfo);

        // Send to monitoring service with checkout context
        if (typeof window !== 'undefined' && (window as any).gtag) {
          (window as any).gtag('event', 'exception', {
            description: `Checkout Error: ${error.message}`,
            fatal: true, // Checkout errors are critical
            custom_map: { checkout_error: true }
          });
        }

        // Could also send to payment monitoring service
        // logPaymentError(error, { context: 'checkout_flow' });
      }}
      errorTitle="Checkout Error"
      errorMessage="We encountered an issue with your checkout. No payment has been processed."
      maxRetries={1} // Fewer retries for checkout (payment-critical)
      showErrorDetails={true}
    >
      {children}
    </ErrorBoundary>
  );
};
